import { useState, useMemo, useCallback, useEffect } from "react"
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button, ExportButton, Checkbox } from "~/ui-rtk/components/ui/controls"
import { formatNumber } from "~/ui-rtk/utils/format"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import {
  getInitiativeTypeLabel,
  type CustomInitiativeTypeMeta,
} from "~/ui-rtk/constants/initiative-types"
import { useCustomAttributionControllerGetInitiativeTypesQuery } from "~/ui-rtk/api/customAttributionApi"
import type { BudgetRecapRequest } from "~/ui-rtk/api/budgetRecommenderApi"
import {
  useLazyGetGoogleAdsScaleSuggestionQuery,
  type GoogleAdsScaleSuggestion,
} from "~/ui-rtk/api/paidMediaApi"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { exportReviewChangesCsv } from "../csv"
import type { AllocationSummary, PoolTotals } from "../allocation"
import {
  buildRecapRequest,
  previewResults,
  unchangedReasonFor,
  type RecapSnapshot,
} from "../recap"
import {
  LARGE_CHANGE_PCT,
  type RecommendationRow,
  type ApplyResult,
  type TroasChange,
} from "../types"
import { isLargeSwing, isTroasScaleCandidate } from "../budget-utils"
import {
  compareGuardrail,
  formatGuardrailValue,
  type GuardrailPerformance,
} from "~/ui-rtk/utils/spend-guardrail"
import RecapPanel from "./RecapPanel"

/** What the modal hands the approve flow for combined budget+tROAS rows. */
export interface SmartScalePlan {
  companyId: string
  troasChanges: Map<string, TroasChange>
}

type Phase = "review" | "applying" | "results"
type ReviewTab = "entity" | "summary"

interface ApprovalModalProps {
  isOpen: boolean
  rows: RecommendationRow[]
  /**
   * Active rows that stay at their current budget (unchecked, or labeled
   * and left out). Shown under the changing rows so the review covers the
   * whole pool the summary totals, exported with the CSV, recorded in the
   * batch audit as skipped, and handed to the recap with a reason.
   */
  unchangedRows?: RecommendationRow[]
  editOverrides: Map<string, number>
  brandSpendPct: number
  targetDailyBudget: number
  targetDRRoas: number
  targetBrandRoas: number
  /**
   * The allocation pool behind the rows: what the unchecked rows commit at
   * their current budgets and what that leaves. Without it the summary can
   * only compare the selected rows to the full target, which reads as a
   * shortfall when the rest of the account is simply staying put.
   */
  allocation?: AllocationSummary | null
  /** The pool as it will be applied — hand edits folded in. */
  pool?: PoolTotals | null
  performanceMetrics: GuardrailPerformance
  /**
   * Average first order value; converts the aMER guardrail to CAC when the
   * customer expresses it that way.
   */
  firstOrderValue?: number | null
  progress: { done: number; total: number }
  onClose: () => void
  onApprove: (
    rows: RecommendationRow[],
    editOverrides: Map<string, number>,
    smartScale?: SmartScalePlan,
    unchangedRows?: RecommendationRow[],
  ) => Promise<ApplyResult[]>
}

const CHANNEL_ORDER = ["Meta", "TikTok", "Pinterest", "Google Ads", "YouTube"]
// Stable default so an omitted `unchangedRows` never re-runs the memos.
const NO_ROWS: RecommendationRow[] = []

function channelIndex(ch: string) {
  const i = CHANNEL_ORDER.indexOf(ch)
  return i === -1 ? 99 : i
}

function sortByChannel(a: { channel: string }, b: { channel: string }) {
  return channelIndex(a.channel) - channelIndex(b.channel)
}

function formatInitiativeType(
  raw: string,
  customTypes?: CustomInitiativeTypeMeta[],
): string {
  if (raw === "UNASSIGNED") return "Unassigned"
  return getInitiativeTypeLabel(raw, customTypes)
}

/** Entity name + IDs cell with bounded width/height so long names can't push
 *  columns out of the modal; the full name shows on hover and in the CSV. */
function EntityNameCell({ row }: { row: RecommendationRow }) {
  const idsLabel = `Campaign ID: ${row.campaignId}${
    row.entityType === "adset" ? ` · Adset ID: ${row.entityId}` : ""
  }`
  return (
    <td className="py-2 px-3 text-white">
      <div
        title={row.entityName}
        className="max-w-[280px] line-clamp-2 [overflow-wrap:anywhere]"
      >
        {row.entityName}
      </div>
      <div
        title={idsLabel}
        className="text-[10px] text-gray-500 max-w-[280px] truncate"
      >
        {idsLabel}
      </div>
    </td>
  )
}

interface SummaryRow {
  key: string
  channel: string
  initiativeType: string
  category: "BRAND" | "DR" | null
  avgDailySpend: number
  newBudget: number
  pctChange: number
  pausedCount: number
  totalPeriodSpend: number
  totalModeledRevenuePeriod: number
  totalBrandValuePeriod: number
}

export default function ApprovalModal({
  isOpen,
  rows,
  editOverrides,
  brandSpendPct,
  targetDailyBudget,
  targetDRRoas,
  targetBrandRoas,
  allocation = null,
  pool = null,
  unchangedRows = NO_ROWS,
  performanceMetrics,
  firstOrderValue,
  progress,
  onClose,
  onApprove,
}: ApprovalModalProps) {
  const [phase, setPhase] = useState<Phase>("review")
  const guardrail = useMemo(
    () => compareGuardrail(performanceMetrics, firstOrderValue),
    [performanceMetrics, firstOrderValue],
  )
  const [results, setResults] = useState<ApplyResult[]>([])
  // Manual-only rows snapshotted at approve time: the parent clears its row
  // selection (our `rows` prop) once apply resolves, so the results phase must
  // not read `manualRows` — a manual-only (e.g. YouTube) apply would render a
  // blank results screen.
  const [resultsManualRows, setResultsManualRows] = useState<
    RecommendationRow[]
  >([])
  // Same reason: the client recap (MD-5417) is written from the rows and
  // budgets the approve was invoked with, frozen at click time.
  const [resultsSnapshot, setResultsSnapshot] = useState<RecapSnapshot | null>(
    null,
  )
  // Review-phase preview of the recap, generated on demand from the rows as
  // they stand — nothing is sent. A new click builds a new request.
  const [previewRequest, setPreviewRequest] =
    useState<BudgetRecapRequest | null>(null)
  const [reviewTab, setReviewTab] = useState<ReviewTab>("entity")
  const [ackLargeSwings, setAckLargeSwings] = useState(false)
  // An over-committed pool scales the selected rows toward $0, which the
  // pause threshold turns into live pauses — and pauses are exempt from the
  // large-swing check. A fresh open is a fresh decision.
  const [ackOverCommit, setAckOverCommit] = useState(false)
  useEffect(() => {
    if (isOpen) setAckOverCommit(false)
  }, [isOpen])
  const [troasSuggestions, setTroasSuggestions] = useState<
    Map<string, GoogleAdsScaleSuggestion>
  >(new Map())
  const [fetchScaleSuggestion] = useLazyGetGoogleAdsScaleSuggestionQuery()
  const currentCompany = useCurrentCompany()
  const companyId = currentCompany?.id ?? ""

  // Google campaigns Google itself reports as tROAS-limited (not budget-
  // limited) whose budget is being raised — the combined-move candidates.
  const troasCandidates = useMemo(
    () =>
      rows.filter(row => {
        const newBudget = editOverrides.get(row.entityId) ?? row.adjustedBudget
        return isTroasScaleCandidate(row) && newBudget > row.currentDailyBudget
      }),
    [rows, editOverrides],
  )

  // Size each candidate's tROAS move when the review opens. The server
  // pre-clamps to the bid-target guardrails; blockers (learning/cooldown)
  // downgrade the row to budget-only with the reason shown.
  useEffect(() => {
    if (!isOpen || !companyId || troasCandidates.length === 0) return
    let cancelled = false
    ;(async () => {
      for (const row of troasCandidates) {
        const newBudget = editOverrides.get(row.entityId) ?? row.adjustedBudget
        try {
          const currentSpendCents = Math.round(row.avgDailySpend * 100)
          const desiredSpendCents = Math.round(newBudget * 100)
          // The sizing is a RATIO of current to desired spend, so a campaign
          // with no spend history has no baseline to scale from. Clamping the
          // numerator to 1 cent would make the ratio arbitrary and always
          // return the maximum cut — leave the row budget-only instead.
          if (currentSpendCents < 1 || desiredSpendCents < 1) {
            if (!cancelled) {
              setTroasSuggestions(prev =>
                new Map(prev).set(row.campaignId, {
                  supported: false,
                  reason: "no recent spend to scale from",
                }),
              )
            }
            continue
          }
          const suggestion = await fetchScaleSuggestion({
            company: companyId,
            campaign: row.campaignId,
            currentDailySpendCents: currentSpendCents,
            desiredDailySpendCents: desiredSpendCents,
          }).unwrap()
          if (!cancelled) {
            setTroasSuggestions(prev =>
              new Map(prev).set(row.campaignId, suggestion),
            )
          }
        } catch {
          // Record the failure rather than leaving the row on "sizing…"
          // forever — an unresolved row blocks Approve (see sizingInFlight).
          if (!cancelled) {
            setTroasSuggestions(prev =>
              new Map(prev).set(row.campaignId, {
                supported: false,
                reason: "could not size a target move",
              }),
            )
          }
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOpen, companyId, troasCandidates, editOverrides, fetchScaleSuggestion])

  // Only unblocked, real moves become part of the approval.
  const troasChanges = useMemo(() => {
    const map = new Map<string, TroasChange>()
    for (const row of troasCandidates) {
      const s = troasSuggestions.get(row.campaignId)
      if (
        s?.supported &&
        !s.portfolio &&
        (s.blockers?.length ?? 0) === 0 &&
        s.currentTargetRoas !== undefined &&
        s.suggestedTargetRoas !== undefined &&
        s.suggestedTargetRoas !== s.currentTargetRoas &&
        s.source
      ) {
        map.set(row.campaignId, {
          fromTargetRoas: s.currentTargetRoas,
          toTargetRoas: s.suggestedTargetRoas,
          source: s.source,
        })
      }
    }
    return map
  }, [troasCandidates, troasSuggestions])

  const { data: detailedTypes = [] } =
    useCustomAttributionControllerGetInitiativeTypesQuery()
  const customTypes = useMemo(
    () => detailedTypes.filter(t => t.isCustom),
    [detailedTypes],
  )

  // Reset phase when modal opens
  const handleClose = useCallback(() => {
    setPhase("review")
    setReviewTab("entity")
    setResults([])
    setResultsManualRows([])
    setResultsSnapshot(null)
    setPreviewRequest(null)
    setAckLargeSwings(false)
    setTroasSuggestions(new Map())
    onClose()
  }, [onClose])

  // Get budget for a row (edit override or recommended)
  const getBudget = useCallback(
    (row: RecommendationRow) =>
      editOverrides.get(row.entityId) ?? row.adjustedBudget,
    [editOverrides],
  )

  // Non-pause budget changes whose magnitude exceeds the confirm threshold, vs
  // the row's current budget. A pause (-100%) is an explicit action and never
  // counts here. These gate the Approve button behind an explicit acknowledgment.
  const largeSwingRows = useMemo(
    () =>
      rows.filter(
        r =>
          !r.shouldPause &&
          r.canApplyBudget &&
          isLargeSwing(r.currentDailyBudget, getBudget(r)),
      ),
    [rows, getBudget],
  )
  const needsSwingAck = largeSwingRows.length > 0 && !ackLargeSwings

  // Suggestions are fetched one row at a time, so Approve is reachable while
  // some are still outstanding. Approving then silently drops those rows to
  // budget-only — the user saw "sizing…" and had no way to know the target
  // move was skipped. Block until every candidate has an answer.
  const sizingInFlight = troasCandidates.some(
    row => !troasSuggestions.has(row.campaignId),
  )

  // Sorted rows by channel
  const sortedRows = useMemo(() => [...rows].sort(sortByChannel), [rows])

  // Manual-only rows (Google/YouTube) need different handling in the UI
  const manualRows = useMemo(
    () => sortedRows.filter(r => !r.canApplyBudget),
    [sortedRows],
  )

  // Budget summary calculations. `rows` are the rows being CHANGED; the
  // pool adds what the unchecked active rows keep, so the total compared to
  // the target is the whole account after approval, not just the subset.
  const summary = useMemo(() => {
    const totalRecommended = rows
      .filter(r => !r.shouldPause)
      .reduce((s, r) => s + getBudget(r), 0)
    const totalAvgDailySpend = rows.reduce((s, r) => s + r.avgDailySpend, 0)
    const unchangedCommitted = pool?.committed ?? 0
    const unchangedCount = pool?.uncheckedCount ?? 0
    const totalAfterApproval = totalRecommended + unchangedCommitted
    const targetDelta = totalAfterApproval - targetDailyBudget
    const spendDelta = totalRecommended - totalAvgDailySpend

    // Category split of the whole pool after approval: the changed rows at
    // their new budgets plus what each category's unchecked rows keep.
    const brandChanging = rows
      .filter(r => r.category === "BRAND" && !r.shouldPause)
      .reduce((s, r) => s + getBudget(r), 0)
    const drChanging = rows
      .filter(r => r.category === "DR" && !r.shouldPause)
      .reduce((s, r) => s + getBudget(r), 0)
    const brandBudget = brandChanging + (allocation?.brand.committed ?? 0)
    const drBudget = drChanging + (allocation?.dr.committed ?? 0)
    const splitTotal = brandBudget + drBudget

    const recommendedBrandPct =
      splitTotal > 0 ? (brandBudget / splitTotal) * 100 : 0
    const recommendedDrPct = splitTotal > 0 ? (drBudget / splitTotal) * 100 : 0

    return {
      totalRecommended,
      totalAvgDailySpend,
      unchangedCommitted,
      unchangedCount,
      totalAfterApproval,
      targetDailyBudget,
      targetDelta,
      spendDelta,
      overCommitted: allocation?.overCommitted ?? false,
      recommendedBrandPct,
      desiredBrandPct: brandSpendPct * 100,
      recommendedDrPct,
      desiredDrPct: (1 - brandSpendPct) * 100,
    }
  }, [rows, getBudget, brandSpendPct, targetDailyBudget, allocation, pool])

  // Selected rows that an over-committed pool scaled into a pause, or that a
  // nearly committed one did (a few dollars left, so the scale-down takes
  // them below the pause threshold). They would be paused live on approve,
  // and pauses are exempt from the large-swing check, so they need their own
  // acknowledgment.
  const overCommitPauseCount = rows.filter(
    r =>
      r.shouldPause &&
      r.canApplyBudget &&
      (summary.overCommitted || r.pausedByScaling),
  ).length
  const showOverCommitAck = summary.overCommitted || overCommitPauseCount > 0
  const needsOverCommitAck = showOverCommitAck && !ackOverCommit

  // Aggregate ROAS from selected rows for performance card
  // Performance vs target is read over the WHOLE active pool — the rows
  // being changed and the ones staying as is — since that is what the
  // target daily budget is spread across.
  const poolRows = useMemo(
    () => [...rows, ...unchangedRows],
    [rows, unchangedRows],
  )

  const aggDrRoas = useMemo(() => {
    const drRows = poolRows.filter(r => r.category === "DR")
    const totalSpend = drRows.reduce((s, r) => s + r.periodSpend, 0)
    const totalRevenue = drRows.reduce((s, r) => s + r.modeledRevenuePeriod, 0)
    return totalSpend > 0 ? totalRevenue / totalSpend : 0
  }, [poolRows])

  const aggBrandRoas = useMemo(() => {
    const brandRows = poolRows.filter(r => r.category === "BRAND")
    const totalSpend = brandRows.reduce((s, r) => s + r.periodSpend, 0)
    const totalBrandValue = brandRows.reduce(
      (s, r) => s + r.brandValuePeriod,
      0,
    )
    return totalSpend > 0 ? totalBrandValue / totalSpend : 0
  }, [poolRows])

  const sortedUnchangedRows = useMemo(
    () => [...unchangedRows].sort(sortByChannel),
    [unchangedRows],
  )
  const [showUnchanged, setShowUnchanged] = useState(false)

  // Channel + Initiative Type aggregated summary rows
  const summaryRows = useMemo(() => {
    const groups = new Map<
      string,
      {
        avgDailySpend: number
        newBudget: number
        channel: string
        initiativeType: string
        category: "BRAND" | "DR" | null
        pausedCount: number
        totalPeriodSpend: number
        totalModeledRevenuePeriod: number
        totalBrandValuePeriod: number
      }
    >()
    for (const row of rows) {
      const key = `${row.channel}||${row.initiativeType}`
      const existing = groups.get(key)
      const budget = getBudget(row)
      const paused = row.shouldPause ? 1 : 0
      if (existing) {
        existing.avgDailySpend += row.avgDailySpend
        existing.newBudget += budget
        existing.pausedCount += paused
        existing.totalPeriodSpend += row.periodSpend
        existing.totalModeledRevenuePeriod += row.modeledRevenuePeriod
        existing.totalBrandValuePeriod += row.brandValuePeriod
      } else {
        groups.set(key, {
          channel: row.channel,
          initiativeType: row.initiativeType,
          category: row.category,
          avgDailySpend: row.avgDailySpend,
          newBudget: budget,
          pausedCount: paused,
          totalPeriodSpend: row.periodSpend,
          totalModeledRevenuePeriod: row.modeledRevenuePeriod,
          totalBrandValuePeriod: row.brandValuePeriod,
        })
      }
    }

    const result: SummaryRow[] = []
    for (const [key, g] of groups) {
      const pctChange =
        g.avgDailySpend > 0
          ? ((g.newBudget - g.avgDailySpend) / g.avgDailySpend) * 100
          : 0
      result.push({
        key,
        channel: g.channel,
        initiativeType: g.initiativeType,
        category: g.category,
        avgDailySpend: g.avgDailySpend,
        newBudget: g.newBudget,
        pctChange,
        pausedCount: g.pausedCount,
        totalPeriodSpend: g.totalPeriodSpend,
        totalModeledRevenuePeriod: g.totalModeledRevenuePeriod,
        totalBrandValuePeriod: g.totalBrandValuePeriod,
      })
    }

    return result.sort((a, b) => {
      const ch = channelIndex(a.channel) - channelIndex(b.channel)
      if (ch !== 0) return ch
      return a.initiativeType.localeCompare(b.initiativeType)
    })
  }, [rows, getBudget])

  const handleExportCsv = useCallback(() => {
    exportReviewChangesCsv(
      sortedRows,
      getBudget,
      raw => formatInitiativeType(raw, customTypes),
      sortedUnchangedRows,
    )
  }, [sortedRows, getBudget, customTypes, sortedUnchangedRows])

  const handleApproveClick = useCallback(async () => {
    // Guard against a rapid double-click firing two apply passes before the
    // phase transition re-renders the button.
    if (phase !== "review") return
    setPhase("applying")
    setResultsManualRows(manualRows)
    setResultsSnapshot({
      rows,
      unchangedRows,
      budgets: new Map(rows.map(r => [r.entityId, getBudget(r)])),
      troasChanges,
      targets: { targetDRRoas, targetBrandRoas, targetDailyBudget },
      mer: performanceMetrics,
    })

    // The hook's approve will handle the actual API calls and update progress
    const applyResults = await onApprove(
      rows,
      editOverrides,
      troasChanges.size > 0 && companyId
        ? { companyId, troasChanges }
        : undefined,
      unchangedRows,
    )
    setResults(applyResults)
    setPhase("results")
  }, [
    phase,
    rows,
    unchangedRows,
    editOverrides,
    onApprove,
    manualRows,
    troasChanges,
    companyId,
    getBudget,
    targetDRRoas,
    targetBrandRoas,
    targetDailyBudget,
    performanceMetrics,
  ])

  // Results summary
  const resultsSummary = useMemo(() => {
    const successCount = results.filter(r => r.success).length
    const failCount = results.filter(r => !r.success).length
    return { successCount, failCount, total: results.length }
  }, [results])

  const handlePreviewRecap = useCallback(() => {
    const snapshot: RecapSnapshot = {
      rows,
      unchangedRows,
      budgets: new Map(rows.map(r => [r.entityId, getBudget(r)])),
      troasChanges,
      targets: { targetDRRoas, targetBrandRoas, targetDailyBudget },
      mer: performanceMetrics,
    }
    setPreviewRequest(
      buildRecapRequest(snapshot, previewResults(snapshot), raw =>
        formatInitiativeType(raw, customTypes),
      ),
    )
  }, [
    rows,
    getBudget,
    troasChanges,
    targetDRRoas,
    targetBrandRoas,
    targetDailyBudget,
    performanceMetrics,
    customTypes,
  ])

  // The recap request, memoised so the panel generates once per approve
  // (a new object would re-run the generation). Null when nothing applied.
  const recapRequest = useMemo(
    () =>
      phase === "results" && resultsSnapshot
        ? buildRecapRequest(resultsSnapshot, results, raw =>
            formatInitiativeType(raw, customTypes),
          )
        : null,
    [phase, resultsSnapshot, results, customTypes],
  )

  return (
    <Dialog
      isOpen={isOpen}
      handleClose={
        phase === "applying"
          ? () => {
              /* noop while applying */
            }
          : handleClose
      }
      width={950}
      classes={{
        container:
          "bg-basic-dark-blue border border-basic-blue rounded-xl p-0 text-left max-h-[85vh] flex flex-col",
      }}
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-basic-blue">
        <h2 className="text-lg font-semibold text-white">
          {phase === "review" && "Review Budget Changes"}
          {phase === "applying" && "Applying Changes..."}
          {phase === "results" && "Results"}
        </h2>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {/* Phase A: Review */}
        {phase === "review" && (
          <>
            {/* Smart scaling: campaigns Google says are tROAS-limited, not
                budget-limited — one approve applies budget AND target. */}
            {troasCandidates.length > 0 && (
              <div className="bg-system-blue-800 rounded-lg p-4 border border-blue-700/40">
                <h3 className="text-sm font-medium text-gray-300 mb-1">
                  Smart Scaling — tROAS-limited Google campaigns
                </h3>
                <p className="text-xs text-gray-400 mb-2">
                  Google reports these campaigns are limited by their tROAS
                  target, not budget. Approving applies the budget change AND
                  the target change shown.
                </p>
                <div className="space-y-1">
                  {troasCandidates.map(row => {
                    const suggestion = troasSuggestions.get(row.campaignId)
                    const change = troasChanges.get(row.campaignId)
                    return (
                      <div
                        key={row.campaignId}
                        className="flex items-center gap-2 text-sm"
                      >
                        <span
                          className="text-white truncate max-w-[280px]"
                          title={row.entityName}
                        >
                          {row.entityName}
                        </span>
                        {!suggestion && (
                          <span className="text-xs text-gray-500">sizing…</span>
                        )}
                        {suggestion && change && (
                          <span className="text-xs text-american-green">
                            tROAS {change.fromTargetRoas.toFixed(2)} →{" "}
                            {change.toTargetRoas.toFixed(2)}
                            <span className="text-gray-500">
                              {" "}
                              ({change.source})
                            </span>
                            {suggestion.reachesDesiredSpend === false && (
                              <span className="text-yellow-300">
                                {" "}
                                — capped by guardrails, will not reach the new
                                budget in one move
                              </span>
                            )}
                          </span>
                        )}
                        {suggestion && !change && (
                          <span className="text-xs text-yellow-300">
                            budget only —{" "}
                            {suggestion.blockers?.[0] ??
                              suggestion.reason ??
                              "no target move available"}
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Performance vs Target Card */}
            <div className="bg-system-blue-800 rounded-lg p-4 space-y-2 border border-blue-700/40">
              <h3 className="text-sm font-medium text-gray-300 mb-3">
                Performance vs. Target
              </h3>
              {guardrail.label && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">{guardrail.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium">
                      {guardrail.actual !== null
                        ? formatGuardrailValue(
                            guardrail.actual,
                            guardrail.display,
                          )
                        : "--"}
                    </span>
                    <span className="text-gray-500">vs</span>
                    <span className="text-gray-400">
                      {formatGuardrailValue(
                        guardrail.target,
                        guardrail.display,
                      )}{" "}
                      target
                    </span>
                    {guardrail.actual !== null && guardrail.target > 0 && (
                      <span
                        className={cn(
                          "text-xs font-medium",
                          (
                            guardrail.lowerIsBetter
                              ? guardrail.actual <= guardrail.target
                              : guardrail.actual >= guardrail.target
                          )
                            ? "text-american-green"
                            : "text-states-error",
                        )}
                      >
                        {(() => {
                          const pct =
                            ((guardrail.actual - guardrail.target) /
                              guardrail.target) *
                            100
                          return `${pct >= 0 ? "+" : ""}${pct.toFixed(0)}%`
                        })()}
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">DR Modeled ROAS</span>
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">
                    {aggDrRoas.toFixed(2)}x
                  </span>
                  <span className="text-gray-500">vs</span>
                  <span className="text-gray-400">
                    {targetDRRoas.toFixed(2)}x target
                  </span>
                  {targetDRRoas > 0 && (
                    <span
                      className={cn(
                        "text-xs font-medium",
                        aggDrRoas >= targetDRRoas
                          ? "text-american-green"
                          : "text-states-error",
                      )}
                    >
                      {(() => {
                        const pct =
                          ((aggDrRoas - targetDRRoas) / targetDRRoas) * 100
                        return `${pct >= 0 ? "+" : ""}${pct.toFixed(0)}%`
                      })()}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Brand Modeled BROAS</span>
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">
                    {aggBrandRoas.toFixed(2)}x
                  </span>
                  <span className="text-gray-500">vs</span>
                  <span className="text-gray-400">
                    {targetBrandRoas.toFixed(2)}x target
                  </span>
                  {targetBrandRoas > 0 && (
                    <span
                      className={cn(
                        "text-xs font-medium",
                        aggBrandRoas >= targetBrandRoas
                          ? "text-american-green"
                          : "text-states-error",
                      )}
                    >
                      {(() => {
                        const pct =
                          ((aggBrandRoas - targetBrandRoas) / targetBrandRoas) *
                          100
                        return `${pct >= 0 ? "+" : ""}${pct.toFixed(0)}%`
                      })()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Budget Summary Card */}
            <div className="bg-system-blue-800 rounded-lg p-4 space-y-2">
              <h3 className="text-sm font-medium text-gray-300 mb-3">
                Budget Summary
              </h3>
              {/* The whole pool after approval: changing rows at their new
                  budgets + unchecked active rows at their current ones. */}
              <div
                className="flex items-center justify-between text-sm"
                data-testid="summary-changing"
              >
                <span className="text-gray-400">Changing ({rows.length})</span>
                <span className="text-white font-medium">
                  {formatNumber(summary.totalRecommended, { currency: true })}
                </span>
              </div>
              <div
                className="flex items-center justify-between text-sm"
                data-testid="summary-unchanged"
              >
                <span className="text-gray-400">
                  Staying as is ({summary.unchangedCount})
                  <span
                    className="ml-1 text-gray-500"
                    title="Active rows you left unchecked keep their current daily budget, which still counts toward the target"
                  >
                    at current budgets
                  </span>
                </span>
                <span className="text-gray-300">
                  {formatNumber(summary.unchangedCommitted, {
                    currency: true,
                  })}
                </span>
              </div>
              <div
                className="flex items-center justify-between text-sm border-t border-basic-blue pt-2"
                data-testid="summary-total"
              >
                <span className="text-gray-400">Total after approval</span>
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">
                    {formatNumber(summary.totalAfterApproval, {
                      currency: true,
                    })}
                  </span>
                  <span className="text-gray-500">vs</span>
                  <span className="text-gray-400">
                    {formatNumber(summary.targetDailyBudget, {
                      currency: true,
                    })}{" "}
                    target
                  </span>
                  <span
                    className={cn(
                      "text-xs font-medium px-1.5 py-0.5 rounded",
                      summary.targetDelta > 0
                        ? "bg-states-error/20 text-states-error"
                        : summary.targetDelta < 0
                          ? "bg-yellow-900/40 text-yellow-300"
                          : "bg-gray-700 text-gray-400",
                    )}
                  >
                    {summary.targetDelta > 0 ? "+" : ""}
                    {formatNumber(summary.targetDelta, { currency: true })}
                  </span>
                </div>
              </div>
              {summary.overCommitted ? (
                <p className="text-xs text-states-error">
                  The unchecked rows alone exceed the target, so the selected
                  rows were scaled toward $0. Check more rows, uncheck fewer, or
                  raise the target before approving.
                </p>
              ) : summary.targetDelta < -1 ? (
                <p className="text-xs text-yellow-300">
                  Lands {formatNumber(-summary.targetDelta, { currency: true })}{" "}
                  under target: recommendations are never scaled above what
                  performance supports, and the unchanged rows keep their
                  current budgets. Check more rows or edit a budget to use the
                  headroom.
                </p>
              ) : summary.targetDelta > 1 ? (
                <p className="text-xs text-states-error">
                  Lands {formatNumber(summary.targetDelta, { currency: true })}{" "}
                  over target.
                </p>
              ) : null}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">vs Current Spend</span>
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">
                    {formatNumber(summary.totalRecommended, { currency: true })}
                  </span>
                  <span className="text-gray-500">vs</span>
                  <span className="text-gray-400">
                    {formatNumber(summary.totalAvgDailySpend, {
                      currency: true,
                    })}{" "}
                    avg daily spend
                  </span>
                  <span
                    className={cn(
                      "text-xs font-medium px-1.5 py-0.5 rounded",
                      summary.spendDelta > 0
                        ? "bg-american-green/20 text-american-green"
                        : summary.spendDelta < 0
                          ? "bg-states-error/20 text-states-error"
                          : "bg-gray-700 text-gray-400",
                    )}
                  >
                    {summary.spendDelta > 0 ? "+" : ""}
                    {formatNumber(summary.spendDelta, { currency: true })}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Brand %</span>
                <span className="text-white">
                  {summary.recommendedBrandPct.toFixed(1)}%
                  <span className="text-gray-500 ml-1">
                    (target: {summary.desiredBrandPct.toFixed(1)}%)
                  </span>
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">DR %</span>
                <span className="text-white">
                  {summary.recommendedDrPct.toFixed(1)}%
                  <span className="text-gray-500 ml-1">
                    (target: {summary.desiredDrPct.toFixed(1)}%)
                  </span>
                </span>
              </div>
            </div>

            {/* Tabs + Changes Tables */}
            <div>
              <div className="flex border-b border-basic-blue mb-2">
                <button
                  className={cn(
                    "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
                    reviewTab === "entity"
                      ? "border-blue-500 text-white"
                      : "border-transparent text-gray-400 hover:text-gray-300",
                  )}
                  onClick={() => setReviewTab("entity")}
                >
                  Campaign / Adset ({sortedRows.length})
                </button>
                <button
                  className={cn(
                    "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
                    reviewTab === "summary"
                      ? "border-blue-500 text-white"
                      : "border-transparent text-gray-400 hover:text-gray-300",
                  )}
                  onClick={() => setReviewTab("summary")}
                >
                  Channel / Initiative Type ({summaryRows.length})
                </button>
              </div>

              {reviewTab === "entity" && (
                <>
                  <div className="border border-basic-blue rounded-lg overflow-x-auto">
                    <table className="table table-sm w-full bg-basic-dark-blue">
                      <thead>
                        <tr className="text-xs text-gray-400 border-b border-basic-blue">
                          <th className="py-2 px-3 text-left font-medium">
                            Entity
                          </th>
                          <th className="py-2 px-3 text-left font-medium">
                            Channel
                          </th>
                          <th className="py-2 px-3 text-right font-medium">
                            Avg Daily Spend
                          </th>
                          <th className="py-2 px-3 text-right font-medium">
                            <div>DR ROAS</div>
                            <div className="text-[10px] text-gray-500 font-normal">
                              vs {targetDRRoas.toFixed(1)}x
                            </div>
                          </th>
                          <th className="py-2 px-3 text-right font-medium">
                            <div>Brand ROAS</div>
                            <div className="text-[10px] text-gray-500 font-normal">
                              vs {targetBrandRoas.toFixed(1)}x
                            </div>
                          </th>
                          <th className="py-2 px-3 text-center font-medium w-8" />
                          <th className="py-2 px-3 text-right font-medium">
                            New
                          </th>
                          <th className="py-2 px-3 text-right font-medium">
                            Change
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.map((row, idx) => {
                          const newBudget = getBudget(row)
                          const pctChange =
                            row.avgDailySpend > 0
                              ? ((newBudget - row.avgDailySpend) /
                                  row.avgDailySpend) *
                                100
                              : 0
                          const drPctVsTarget =
                            targetDRRoas > 0
                              ? ((row.drRoas - targetDRRoas) / targetDRRoas) *
                                100
                              : null
                          const brandPctVsTarget =
                            targetBrandRoas > 0
                              ? ((row.brandRoas - targetBrandRoas) /
                                  targetBrandRoas) *
                                100
                              : null
                          return (
                            <tr
                              key={row.entityId}
                              className={cn(
                                "text-sm",
                                idx % 2 === 0
                                  ? "bg-basic-dark-blue"
                                  : "bg-basic-black",
                              )}
                            >
                              <EntityNameCell row={row} />
                              <td className="py-2 px-3 text-gray-300 text-xs">
                                {row.channel}
                              </td>
                              <td className="py-2 px-3 text-right text-gray-300">
                                {formatNumber(row.avgDailySpend, {
                                  currency: true,
                                })}
                              </td>
                              <td className="py-2 px-3 text-right">
                                {row.category === "DR" ? (
                                  <div className="flex items-center justify-end gap-1">
                                    <span className="font-medium text-white">
                                      {row.drRoas.toFixed(2)}x
                                    </span>
                                    {drPctVsTarget !== null && (
                                      <span
                                        className={cn(
                                          "text-[10px] font-medium",
                                          drPctVsTarget >= 0
                                            ? "text-american-green"
                                            : "text-states-error",
                                        )}
                                      >
                                        {drPctVsTarget >= 0 ? "+" : ""}
                                        {drPctVsTarget.toFixed(0)}%
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-600">--</span>
                                )}
                              </td>
                              <td className="py-2 px-3 text-right">
                                {row.category === "BRAND" ? (
                                  <div className="flex items-center justify-end gap-1">
                                    <span className="font-medium text-white">
                                      {row.brandRoas.toFixed(2)}x
                                    </span>
                                    {brandPctVsTarget !== null && (
                                      <span
                                        className={cn(
                                          "text-[10px] font-medium",
                                          brandPctVsTarget >= 0
                                            ? "text-american-green"
                                            : "text-states-error",
                                        )}
                                      >
                                        {brandPctVsTarget >= 0 ? "+" : ""}
                                        {brandPctVsTarget.toFixed(0)}%
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-600">--</span>
                                )}
                              </td>
                              <td className="py-2 px-3 text-center text-gray-500">
                                &rarr;
                              </td>
                              <td className="py-2 px-3 text-right text-white font-medium">
                                {row.shouldPause ? (
                                  <span className="text-states-error">
                                    Pause
                                  </span>
                                ) : (
                                  formatNumber(newBudget, { currency: true })
                                )}
                              </td>
                              <td className="py-2 px-3 text-right">
                                {row.shouldPause ? (
                                  <span className="text-xs font-medium text-states-error">
                                    Pause
                                  </span>
                                ) : (
                                  <span
                                    className={cn(
                                      "text-xs font-medium",
                                      pctChange > 0
                                        ? "text-american-green"
                                        : pctChange < 0
                                          ? "text-states-error"
                                          : "text-gray-400",
                                    )}
                                  >
                                    {pctChange > 0 ? "+" : ""}
                                    {pctChange.toFixed(1)}%
                                  </span>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* The rest of the pool: active rows staying at their
                      current budget. Collapsed — large accounts carry
                      hundreds — but always counted and always listable. */}
                  {sortedUnchangedRows.length > 0 && (
                    <div
                      className="border border-basic-blue rounded-lg mt-2"
                      data-testid="unchanged-rows"
                    >
                      <button
                        type="button"
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-300 hover:text-white"
                        onClick={() => setShowUnchanged(v => !v)}
                        aria-expanded={showUnchanged}
                      >
                        <span>
                          Unchanged ({sortedUnchangedRows.length}) — staying at
                          their current budgets, counted toward the target
                        </span>
                        <span className="flex items-center gap-3">
                          <span className="text-white font-medium">
                            {formatNumber(
                              sortedUnchangedRows.reduce(
                                (s, r) => s + r.currentDailyBudget,
                                0,
                              ),
                              { currency: true },
                            )}
                            /day
                          </span>
                          <span className="text-xs text-gray-500">
                            {showUnchanged ? "Hide" : "Show"}
                          </span>
                        </span>
                      </button>
                      {showUnchanged && (
                        <div className="overflow-x-auto border-t border-basic-blue">
                          <table className="table table-sm w-full bg-basic-dark-blue">
                            <thead>
                              <tr className="text-xs text-gray-400 border-b border-basic-blue">
                                <th className="py-2 px-3 text-left font-medium">
                                  Entity
                                </th>
                                <th className="py-2 px-3 text-left font-medium">
                                  Channel
                                </th>
                                <th className="py-2 px-3 text-right font-medium">
                                  Avg Daily Spend
                                </th>
                                <th className="py-2 px-3 text-right font-medium">
                                  Current Budget
                                </th>
                                <th className="py-2 px-3 text-left font-medium">
                                  Why unchanged
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedUnchangedRows.map((row, idx) => (
                                <tr
                                  key={row.entityId}
                                  className={cn(
                                    "text-sm",
                                    idx % 2 === 0
                                      ? "bg-basic-dark-blue"
                                      : "bg-basic-black",
                                  )}
                                >
                                  <EntityNameCell row={row} />
                                  <td className="py-2 px-3 text-gray-300 text-xs">
                                    {row.channel}
                                  </td>
                                  <td className="py-2 px-3 text-right text-gray-300">
                                    {formatNumber(row.avgDailySpend, {
                                      currency: true,
                                    })}
                                  </td>
                                  <td className="py-2 px-3 text-right text-white font-medium">
                                    {formatNumber(row.currentDailyBudget, {
                                      currency: true,
                                    })}
                                  </td>
                                  <td className="py-2 px-3 text-xs text-gray-400">
                                    {unchangedReasonFor(row)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {reviewTab === "summary" && (
                <div className="border border-basic-blue rounded-lg overflow-x-auto">
                  <table className="table table-sm w-full bg-basic-dark-blue">
                    <thead>
                      <tr className="text-xs text-gray-400 border-b border-basic-blue">
                        <th className="py-2 px-3 text-left font-medium">
                          Channel
                        </th>
                        <th className="py-2 px-3 text-left font-medium">
                          Initiative Type
                        </th>
                        <th className="py-2 px-3 text-right font-medium">
                          Avg Daily Spend
                        </th>
                        <th className="py-2 px-3 text-right font-medium">
                          <div>DR ROAS</div>
                          <div className="text-[10px] text-gray-500 font-normal">
                            vs {targetDRRoas.toFixed(1)}x
                          </div>
                        </th>
                        <th className="py-2 px-3 text-right font-medium">
                          <div>Brand ROAS</div>
                          <div className="text-[10px] text-gray-500 font-normal">
                            vs {targetBrandRoas.toFixed(1)}x
                          </div>
                        </th>
                        <th className="py-2 px-3 text-center font-medium w-8" />
                        <th className="py-2 px-3 text-right font-medium">
                          New
                        </th>
                        <th className="py-2 px-3 text-right font-medium">
                          Change
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryRows.map((row, idx) => {
                        const groupDrRoas =
                          row.category === "DR" && row.totalPeriodSpend > 0
                            ? row.totalModeledRevenuePeriod /
                              row.totalPeriodSpend
                            : null
                        const groupBrandRoas =
                          row.category === "BRAND" && row.totalPeriodSpend > 0
                            ? row.totalBrandValuePeriod / row.totalPeriodSpend
                            : null
                        return (
                          <tr
                            key={row.key}
                            className={cn(
                              "text-sm",
                              idx % 2 === 0
                                ? "bg-basic-dark-blue"
                                : "bg-basic-black",
                            )}
                          >
                            <td className="py-2 px-3 text-gray-300 text-xs">
                              {row.channel}
                            </td>
                            <td className="py-2 px-3 text-white">
                              {formatInitiativeType(
                                row.initiativeType,
                                customTypes,
                              )}
                              {row.pausedCount > 0 && (
                                <span className="text-xs text-states-error ml-1">
                                  ({row.pausedCount} paused)
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-right text-gray-300">
                              {formatNumber(row.avgDailySpend, {
                                currency: true,
                              })}
                            </td>
                            {(() => {
                              const drPct =
                                groupDrRoas !== null && targetDRRoas > 0
                                  ? ((groupDrRoas - targetDRRoas) /
                                      targetDRRoas) *
                                    100
                                  : null
                              return (
                                <td className="py-2 px-3 text-right">
                                  {groupDrRoas !== null && drPct !== null ? (
                                    <div className="flex items-center justify-end gap-1">
                                      <span className="font-medium text-white">
                                        {groupDrRoas.toFixed(2)}x
                                      </span>
                                      <span
                                        className={cn(
                                          "text-[10px] font-medium",
                                          drPct >= 0
                                            ? "text-american-green"
                                            : "text-states-error",
                                        )}
                                      >
                                        {drPct >= 0 ? "+" : ""}
                                        {drPct.toFixed(0)}%
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-gray-600">--</span>
                                  )}
                                </td>
                              )
                            })()}
                            {(() => {
                              const brandPct =
                                groupBrandRoas !== null && targetBrandRoas > 0
                                  ? ((groupBrandRoas - targetBrandRoas) /
                                      targetBrandRoas) *
                                    100
                                  : null
                              return (
                                <td className="py-2 px-3 text-right">
                                  {groupBrandRoas !== null &&
                                  brandPct !== null ? (
                                    <div className="flex items-center justify-end gap-1">
                                      <span className="font-medium text-white">
                                        {groupBrandRoas.toFixed(2)}x
                                      </span>
                                      <span
                                        className={cn(
                                          "text-[10px] font-medium",
                                          brandPct >= 0
                                            ? "text-american-green"
                                            : "text-states-error",
                                        )}
                                      >
                                        {brandPct >= 0 ? "+" : ""}
                                        {brandPct.toFixed(0)}%
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-gray-600">--</span>
                                  )}
                                </td>
                              )
                            })()}
                            <td className="py-2 px-3 text-center text-gray-500">
                              &rarr;
                            </td>
                            <td className="py-2 px-3 text-right text-white font-medium">
                              {formatNumber(row.newBudget, { currency: true })}
                            </td>
                            <td className="py-2 px-3 text-right">
                              <span
                                className={cn(
                                  "text-xs font-medium",
                                  row.pctChange > 0
                                    ? "text-american-green"
                                    : row.pctChange < 0
                                      ? "text-states-error"
                                      : "text-gray-400",
                                )}
                              >
                                {row.pctChange > 0 ? "+" : ""}
                                {row.pctChange.toFixed(1)}%
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {manualRows.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {manualRows.length}{" "}
                  {[...new Set(manualRows.map(r => r.channel))].join("/")}{" "}
                  {manualRows.length === 1 ? "entity" : "entities"} will require
                  manual updates in the respective platform.
                </p>
              )}
            </div>
          </>
        )}

        {/* Review-phase recap preview (nothing sent) */}
        {phase === "review" && previewRequest && (
          <RecapPanel request={previewRequest} preview />
        )}

        {/* Phase B: Applying */}
        {phase === "applying" && (
          <div className="py-8 space-y-4">
            <div className="w-full bg-basic-black rounded-full h-3">
              <div
                className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                style={{
                  width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%`,
                }}
              />
            </div>
            <p className="text-center text-sm text-gray-400">
              Applying budgets... {progress.done}/{progress.total}
            </p>
          </div>
        )}

        {/* Phase C: Results */}
        {phase === "results" && (
          <>
            {/* Applied changes */}
            {results.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-300 mb-2">
                  Applied Changes
                </h3>
                <p className="text-sm text-gray-400 mb-3">
                  {resultsSummary.successCount} of {resultsSummary.total}{" "}
                  budgets updated successfully
                  {resultsSummary.failCount > 0 && (
                    <span className="text-states-error">
                      {" "}
                      ({resultsSummary.failCount} failed)
                    </span>
                  )}
                </p>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {results.map(r => (
                    <div
                      key={r.entityId}
                      className="flex items-center gap-2 text-sm py-1"
                    >
                      {r.success ? (
                        <CheckCircleIcon className="w-4 h-4 text-american-green shrink-0" />
                      ) : (
                        <XCircleIcon className="w-4 h-4 text-states-error shrink-0" />
                      )}
                      <span className="text-white break-words min-w-0">
                        {r.entityName}
                      </span>
                      <span className="text-xs text-gray-500">{r.channel}</span>
                      {r.paused && r.success && (
                        <span className="text-xs font-medium text-states-error ml-auto">
                          Paused
                        </span>
                      )}
                      {r.error && (
                        <span className="text-xs text-states-error ml-auto">
                          {r.error}
                        </span>
                      )}
                      {r.success && r.troasApplied === true && (
                        <span className="text-xs text-american-green ml-auto">
                          + tROAS applied
                        </span>
                      )}
                      {r.success && r.troasError && (
                        <span className="text-xs text-yellow-300 ml-auto">
                          Budget applied; tROAS failed: {r.troasError}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Client recap: the story behind the applied changes */}
            {recapRequest && <RecapPanel request={recapRequest} />}

            {/* Manual changes needed */}
            {resultsManualRows.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-yellow-300 mb-2">
                  Manual Changes Needed
                </h3>
                <p className="text-xs text-gray-400 mb-2">
                  These budgets must be updated manually in the respective
                  platform.
                </p>
                <div className="border border-basic-blue rounded-lg overflow-x-auto">
                  <table className="table table-sm w-full bg-basic-dark-blue">
                    <thead>
                      <tr className="text-xs text-gray-400 border-b border-basic-blue">
                        <th className="py-2 px-3 text-left font-medium">
                          Entity
                        </th>
                        <th className="py-2 px-3 text-left font-medium">
                          Channel
                        </th>
                        <th className="py-2 px-3 text-right font-medium">
                          Recommended Budget
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultsManualRows.map((row, idx) => (
                        <tr
                          key={row.entityId}
                          className={cn(
                            "text-sm",
                            idx % 2 === 0
                              ? "bg-basic-dark-blue"
                              : "bg-basic-black",
                          )}
                        >
                          <EntityNameCell row={row} />
                          <td className="py-2 px-3 text-gray-300 text-xs">
                            {row.channel}
                          </td>
                          <td className="py-2 px-3 text-right text-white font-medium">
                            {formatNumber(getBudget(row), { currency: true })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-basic-blue flex justify-end gap-3">
        {phase === "review" && (
          <>
            <ExportButton onExport={handleExportCsv} />
            <Button
              variant={{ variant: "outlined" }}
              size="sm"
              type="button"
              className="mr-auto"
              onClick={handlePreviewRecap}
              disabled={!rows.some(r => r.canApplyBudget)}
            >
              Preview recap
            </Button>
            {showOverCommitAck && (
              <label
                className="flex items-center gap-2 text-xs text-states-error mr-2 max-w-xs"
                data-testid="over-commit-ack"
              >
                <Checkbox
                  checked={ackOverCommit}
                  onChange={e => setAckOverCommit(e.target.checked)}
                />
                <span>
                  {summary.overCommitted
                    ? "Unchecked rows already exceed the target"
                    : "Unchecked rows leave too little of the target"}
                  , so{" "}
                  {overCommitPauseCount === 1
                    ? "1 selected row will be paused"
                    : `${overCommitPauseCount} selected rows will be paused`}{" "}
                  — I understand
                </span>
              </label>
            )}
            {largeSwingRows.length > 0 && (
              <label className="flex items-center gap-2 text-xs text-yellow-300 mr-2 max-w-xs">
                <Checkbox
                  checked={ackLargeSwings}
                  onChange={e => setAckLargeSwings(e.target.checked)}
                />
                <span>
                  {largeSwingRows.length} budget
                  {largeSwingRows.length > 1 ? "s" : ""} change by &gt;
                  {LARGE_CHANGE_PCT}% — I&apos;ve reviewed them
                </span>
              </label>
            )}
            <Button
              variant={{ variant: "outlined" }}
              size="sm"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant={{ variant: "solid", color: "blue" }}
              size="sm"
              onClick={handleApproveClick}
              disabled={needsSwingAck || needsOverCommitAck || sizingInFlight}
            >
              Approve & Apply
            </Button>
          </>
        )}
        {phase === "applying" && (
          <Button variant={{ variant: "outlined" }} size="sm" disabled>
            Applying...
          </Button>
        )}
        {phase === "results" && (
          <Button
            variant={{ variant: "solid", color: "blue" }}
            size="sm"
            onClick={handleClose}
          >
            Done
          </Button>
        )}
      </div>
    </Dialog>
  )
}
